import { useEffect, useRef } from 'react';
import { videoObjectJSON } from '@expPlatform/microsites/utils/google';
import { logWarning } from '@core/logging';
/**
 * Component that generates JSON-LD schema for the first video in feedVideos
 * and adds it to the document head. If JSON-LD already exists, it enhances
 * the existing schema with video data.
 */
export const VideoFeedJsonLd = ({ feedVideos, }) => {
    // Store the original JSON-LD content to restore on unmount
    const originalJsonLdRef = useRef();
    const hadOriginalJsonLdRef = useRef(false);
    useEffect(() => {
        if (!feedVideos?.length) {
            return;
        }
        const firstVideo = feedVideos[0];
        if (!firstVideo) {
            return;
        }
        // Check if JSON-LD script already exists and store original content
        const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
        if (existingJsonLd) {
            // Store the original content only on first mount
            if (originalJsonLdRef.current === null) {
                originalJsonLdRef.current = existingJsonLd.textContent ?? undefined;
                hadOriginalJsonLdRef.current = true;
            }
        }
        else {
            // No existing JSON-LD found
            hadOriginalJsonLdRef.current = false;
        }
        // Generate video JSON-LD schema using existing utility
        const videoSchemaString = videoObjectJSON(firstVideo);
        const videoSchema = JSON.parse(videoSchemaString);
        if (existingJsonLd) {
            // Enhance existing JSON-LD with video data
            try {
                const existingData = JSON.parse(existingJsonLd.textContent || '{}');
                const enhancedData = enhanceExistingJsonLd(existingData, videoSchema);
                existingJsonLd.textContent = JSON.stringify(enhancedData);
            }
            catch (error) {
                logWarning('Failed to parse existing JSON-LD, replacing with video schema:', error);
                existingJsonLd.textContent = JSON.stringify(videoSchema);
            }
        }
        else {
            // Create new JSON-LD script element
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.textContent = JSON.stringify(videoSchema);
            document.head.appendChild(script);
        }
        // Cleanup function to restore original JSON-LD or remove created script
        return () => {
            const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
            if (scriptToRemove) {
                if (hadOriginalJsonLdRef.current &&
                    originalJsonLdRef.current !== null) {
                    // Restore the original JSON-LD content
                    scriptToRemove.textContent = originalJsonLdRef.current ?? '';
                }
                else {
                    // Remove the script we created (no original existed)
                    scriptToRemove.remove();
                }
            }
        };
    }, [feedVideos]);
    return null; // This component doesn't render anything visible
};
/**
 * Enhances existing JSON-LD data with video schema
 */
function enhanceExistingJsonLd(existingData, videoSchema) {
    // If existing data is an array, add video schema to it
    if (Array.isArray(existingData)) {
        // Check if video schema already exists
        const hasVideoSchema = existingData.some((item) => item['@type'] === 'VideoObject' &&
            item.contentUrl === videoSchema.contentUrl);
        if (!hasVideoSchema) {
            return [...existingData, videoSchema];
        }
        return existingData;
    }
    // If existing data is a single object
    if (typeof existingData === 'object' && existingData !== null) {
        // If it's already a VideoObject, merge the data
        if (existingData['@type'] === 'VideoObject') {
            return { ...existingData, ...videoSchema };
        }
        // Otherwise, create an array with both schemas
        return [existingData, videoSchema];
    }
    // Fallback to just the video schema
    return videoSchema;
}
